import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { SeletorGrupoTipo } from '@/components/templates/seletor-grupo-tipo';
import { TabelaPartes } from '@/components/apreciacao/tabela-partes';
import { ModalEditarParte } from '@/components/apreciacao/modal-editar-parte';

export function TemplateCreate() {
    const navigate = useNavigate();
    const { projeto } = useProjeto();
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);

    // Meta dados do template
    const [templateMeta, setTemplateMeta] = useState({
        nome: '',
        descricao: '',
        norma: 'NR-12'
    });

    const [tipoSelecionado, setTipoSelecionado] = useState<any | null>(null);

    // Estrutura de Partes e Riscos
    const [riscos, setRiscos] = useState<any[]>([]);
    const [modalParteAberto, setModalParteAberto] = useState(false);
    const [parteSelecionada, setParteSelecionada] = useState<{ parte: string; riscos: any[]; numero: number } | null>(null);

    const handleChangeMeta = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setTemplateMeta(prev => ({ ...prev, [name]: value }));
    };

    const handleAdicionarParte = () => {
        const partesUnicas = new Set(riscos.map(r => r.parte || ''));
        const proximoNumero = partesUnicas.size + 1;
        let nomeParte = `Parte ${proximoNumero}`;
        let contador = 1;
        while (partesUnicas.has(nomeParte)) {
            contador++;
            nomeParte = `Parte ${proximoNumero} (${contador})`;
        }
        setParteSelecionada({ parte: nomeParte, riscos: [], numero: proximoNumero });
        setModalParteAberto(true);
    };

    const handleEditarParte = (parte: string, riscosParte: any[]) => {
        const partesUnicas = Array.from(new Set(riscos.map(r => r.parte || '')));
        const ind = partesUnicas.indexOf(parte);
        const numero = ind >= 0 ? ind + 1 : partesUnicas.length + 1;
        setParteSelecionada({ parte, riscos: riscosParte, numero });
        setModalParteAberto(true);
    };

    const handleDeletarParte = (parte: string) => {
        setRiscos(riscos.filter(r => r.parte !== parte));
    };

    const handleDuplicarParte = (parte: string, riscosParte: any[]) => {
        const partesExistentes = new Set(riscos.map(r => r.parte || ''));
        let novoNomeParte = `${parte} (Cópia)`;
        let contador = 1;

        while (partesExistentes.has(novoNomeParte)) {
            contador++;
            novoNomeParte = `${parte} (Cópia ${contador})`;
        }

        const maxNumero = riscos.length > 0 ? Math.max(...riscos.map(r => r.numero || 0)) : 0;
        let proximoNumero = maxNumero + 1;

        const riscosDuplicados = riscosParte.map((r: any) => ({
            ...r,
            id: crypto.randomUUID(),
            numero: proximoNumero++,
            parte: novoNomeParte,
        }));
        setRiscos([...riscos, ...riscosDuplicados]);
    };

    const handleSalvarParte = async (dadosParte: any) => {
        const novoNomeParte = dadosParte.parte || parteSelecionada?.parte;
        const parteAntiga = dadosParte.parteAntiga || parteSelecionada?.parte;

        let novosRiscos = riscos.filter(r => r.parte !== parteAntiga);

        const maxNumero = novosRiscos.length > 0 ? Math.max(...novosRiscos.map(r => r.numero || 0)) : 0;
        let currentNum = maxNumero + 1;

        const riscosProcessar = dadosParte.riscos?.length > 0 ? dadosParte.riscos : [
            { id: crypto.randomUUID(), tipoPerigo: 'Mecânico', categoria: '', probabilidadeAtual: 1, frequenciaAtual: 1, gravidadeAtual: 1, exposicaoAtual: 1, hrnAtual: 1, conformeAtual: false }
        ];

        const riscosAtualizados = riscosProcessar.map((r: any) => ({
            ...r,
            id: r.id || crypto.randomUUID(),
            numero: r.numero || currentNum++,
            parte: novoNomeParte,
            fotoAtualUrl1: dadosParte.fotoParte1,
            fotoAtualUrl2: dadosParte.fotoParte2,
            fotoAtualUrl3: dadosParte.fotoParte3,
            fotoAtualUrl4: dadosParte.fotoParte4,
            cicloVida: dadosParte.ciclosVida?.join(', '),
            eventoPerigoso: dadosParte.eventoPerigoso,
            conformeAtual: dadosParte.conforme,
            tipoPerigo: dadosParte.tiposPerigo?.join(', '),
            perigosEncontrados: dadosParte.perigosEncontrados?.join(', '),
            consequencias: dadosParte.potenciaisConsequencias?.join(', '),
            potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas,
            origemEspecifica: dadosParte.origemEspecifica,
            partesCorpoAtingidas: dadosParte.partesCorpoAtingidas,
            adequacoesTecnicas: dadosParte.descricaoCondicaoAtual?.join('\n'),
            medidasControle: dadosParte.medidasControle?.join('\n'),
            exigenciasNormativas: dadosParte.exigenciasNormativas?.join('\n'),
            adequacoesNormativas: dadosParte.adequacoesNormativas?.join('\n'),
            pronta: dadosParte.pronta,
            exibirHRN: dadosParte.exibirHRN !== undefined ? dadosParte.exibirHRN : r.exibirHRN,
            exibirSRPCS: dadosParte.exibirSRPCS !== undefined ? dadosParte.exibirSRPCS : r.exibirSRPCS,
        }));

        setRiscos([...novosRiscos, ...riscosAtualizados]);
        setModalParteAberto(false);
    };

    const handleFecharModal = () => {
        setModalParteAberto(false);
        setParteSelecionada(null);
    };

    const handleSalvar = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!projeto) {
            alert(t('templates.criar.erroSemProjeto'));
            return;
        }

        if (!templateMeta.nome || !tipoSelecionado || !templateMeta.norma) {
            alert(t('templates.criar.erroCamposObrigatorios'));
            return;
        }

        setLoading(true);

        try {
            // A lista já está flat
            const riscosPadrao = riscos;

            const payload = {
                nome: templateMeta.nome,
                tipo: tipoSelecionado.nome, // Salvamos o nome do Tipo selecionado para compatibilidade com a tabela Template
                descricao: templateMeta.descricao,
                norma: templateMeta.norma,
                projetoId: projeto.id,
                configuracao: {}, // Deixamos vazio pois simplificamos a tela
                riscosPadrao,
                ativo: true
            };

            const response = await apiService.criarTemplate(payload);

            if (response.success && response.data) {
                alert(t('templates.criar.sucesso'));
                navigate('/meus-templates');
            } else {
                const errorMessage = response.errors?.length
                    ? response.errors.join('\n')
                    : response.message || t('templates.criar.erroCriar');
                alert(t('templates.criar.erroCriarDetalhe', { mensagem: errorMessage }));
            }
        } catch (error) {
            console.error('Erro ao salvar template:', error);
            alert(t('templates.estado.erroSalvar'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
                <div className="flex items-center">
                    <Link
                        to="/meus-templates"
                        className="mr-4 p-2.5 rounded-lg text-schmersal-gray-medium hover:text-schmersal-blue hover:bg-schmersal-gray-light transition-colors duration-200"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">{t('templates.criar.titulo')}</h1>
                        <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                            {t('templates.criar.subtitulo')}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSalvar} className="space-y-6">
                {/* Dados base do Template */}
                <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
                    <h2 className="text-xl font-bold text-schmersal-blue dark:text-white mb-4">{t('templates.form.secaoInformacoes')}</h2>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.nome')}</label>
                            <input
                                type="text"
                                name="nome"
                                required
                                value={templateMeta.nome}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                                placeholder={t('templates.criar.nomePlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.norma')}</label>
                            <select
                                name="norma"
                                required
                                value={templateMeta.norma}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                            >
                                <option value="NR-12">NR-12</option>
                                <option value="ISO 12100">ISO 12100</option>
                                <option value="ISO 23125">ISO 23125</option>
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white">{t('templates.form.descricao')}</label>
                            <textarea
                                name="descricao"
                                rows={2}
                                value={templateMeta.descricao}
                                onChange={handleChangeMeta}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-schmersal-blue focus:border-schmersal-blue sm:text-sm"
                                placeholder={t('templates.criar.descricaoPlaceholder')}
                            />
                        </div>
                    </div>
                </div>

                {/* Seleção de Grupo e Tipo */}
                {projeto && (
                    <SeletorGrupoTipo
                        projetoId={projeto.id}
                        onTipoSelecionado={(tipo) => setTipoSelecionado(tipo)}
                    />
                )}

                {/* Lista de Partes */}
                <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-schmersal-blue dark:text-white">{t('templates.partes.titulo')}</h2>
                            <p className="text-sm text-gray-500 mt-1">{t('templates.criar.partesSubtitulo')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={handleAdicionarParte}
                            className="inline-flex items-center px-4 py-2 bg-blue-50 text-schmersal-blue border border-blue-200 rounded-md hover:bg-blue-100 font-medium text-sm transition-colors"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            {t('templates.partes.adicionar')}
                        </button>
                    </div>

                    <TabelaPartes
                        riscos={riscos}
                        onAdicionar={handleAdicionarParte}
                        onEditar={handleEditarParte}
                        onDeletar={handleDeletarParte}
                        onDuplicar={handleDuplicarParte}
                        modoEdicao={true}
                    />

                    {modalParteAberto && parteSelecionada && (
                        <ModalEditarParte
                            aberto={modalParteAberto}
                            parte={parteSelecionada.parte}
                            riscos={parteSelecionada.riscos}
                            numeroParte={parteSelecionada.numero}
                            onFechar={handleFecharModal}
                            onSalvar={handleSalvarParte}
                            descricaoMaquina={templateMeta.nome || ''}
                        />
                    )}
                </div>

                {/* Actions */}
                <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t-2 border-schmersal-gray-light p-4 shadow-xl flex justify-end gap-4 sm:px-6 lg:px-8">
                    <Link
                        to="/meus-templates"
                        className="w-full sm:w-auto text-center bg-white py-2.5 px-5 border border-rg-line rounded-lg shadow-sm text-sm font-semibold text-schmersal-gray-dark hover:bg-schmersal-gray-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue transition-all duration-200"
                    >
                        {t('comum.acao.cancelar')}
                    </Link>
                    <button
                        type="submit"
                        disabled={loading || !tipoSelecionado}
                        className="w-full sm:w-auto inline-flex justify-center items-center px-5 py-2.5 border-2 border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                        <Save className="h-5 w-5 mr-2" />
                        {loading ? t('comum.acao.salvando') : t('templates.criar.salvarTemplate')}
                    </button>
                </div>
            </form>
        </div>
    );
}
